import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useCallback, type FC } from "react";
import { HeadsUpSidebarContent } from "./heads-up-sidebar-content";
import { useHeadsUpSidebar } from "./hooks";
import { HeadsUpContextProvider } from "@/components/heads-up/context/heads-up-context";
import {
  SIDE_PANEL_COLLAPSED_HEIGHT_PX,
  SIDE_PANEL_COLLAPSED_BUFFER_PX,
} from "@/app/router/constants";

interface HeadsUpSidebarProps {
  hideChrome?: boolean;
  presentation?: "sidebar" | "floating";
  reserveBottomForChat?: boolean;
}

export const HeadsUpSidebar: FC<HeadsUpSidebarProps> = observer(function HeadsUpSidebar({
  hideChrome = false,
  presentation = "sidebar",
  reserveBottomForChat = true,
}) {
  const model = useHeadsUpSidebar();
  const { context, ui } = model;
  const { setHovered } = ui;
  const shouldReserveBottomForChat = !hideChrome && reserveBottomForChat;

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
  }, [setHovered]);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
  }, [setHovered]);

  if (!context.isDatabaseReady) {
    return (
      <Container
        $reserveBottomForChat={shouldReserveBottomForChat}
        $presentation={presentation}
        data-heads-up-sidebar
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    );
  }

  return (
    <Container
      $reserveBottomForChat={shouldReserveBottomForChat}
      $presentation={presentation}
      data-heads-up-sidebar
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <HeadsUpContextProvider model={model}>
        <HeadsUpSidebarContent
          hideChrome={hideChrome}
          presentation={presentation}
          reserveBottomForChat={shouldReserveBottomForChat}
        />
      </HeadsUpContextProvider>
    </Container>
  );
});

const Container = styled.div<{
  $reserveBottomForChat: boolean;
  $presentation: "sidebar" | "floating";
}>`
  display: flex;
  flex-direction: column;
  height: ${({ $reserveBottomForChat }) =>
    $reserveBottomForChat
      ? `calc(100% - ${SIDE_PANEL_COLLAPSED_HEIGHT_PX}px + ${SIDE_PANEL_COLLAPSED_BUFFER_PX}px)`
      : "100%"};
  overflow: ${({ $presentation }) => ($presentation === "floating" ? "hidden" : "auto")};
`;
